// src/pages/ITP.tsx — Inspection & Test Plans with hold points and NCRs
import { useState, useEffect } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_ITP, MOCK_NCRS } from '../utils/mockDataTier3'
import { formatDate } from '../utils/formatters'
import { CheckSquare, AlertTriangle, CheckCircle, X, ArrowLeft, Clock, Lock, Eye } from 'lucide-react'
import { setHandoverReadyFlag } from '../utils/workflowEvents'
import { isWICompletedForJob } from '../utils/workflowEvents'
import { fireHoldPointReleaseAlert } from '../services/remainingModuleConnectors'
import { useQuery } from '@tanstack/react-query'
import { api } from '../utils/api'
import { fetchITPRecords, witnessITPRecord as witnessITPNeon } from '../services/itpService'

type InspectionType = 'HOLD_POINT' | 'WITNESS_POINT' | 'REVIEW' | 'MONITORING'
type InspectionResult = 'PASS' | 'FAIL' | 'CONDITIONAL'

type ITPItem = {
  id: string
  itemNumber: string
  activity: string
  reference: string
  acceptanceCriteria: string
  frequency: string
  inspectionType: InspectionType
  inspectorRole: string
  latestResult: InspectionResult | null
  latestInspector: string | null
  latestDate: string | null
  holdPointReleased: boolean
  releasedBy: string | null
  releasedAt: string | null
  notes: string | null
}

type ITPRecord = {
  id: string
  itpNumber: string
  jobId: string
  jobNumber: string
  title: string
  revision: number
  status: string
  approvedById: string
  approvedByName: string
  items: ITPItem[]
}

const INSP_TYPE: Record<string, { label: string; bg: string; text: string; icon: any; desc: string }> = {
  HOLD_POINT:    { label: 'Hold point',    bg: 'bg-red-100',    text: 'text-red-700',    icon: Lock,         desc: 'Work CANNOT proceed until released' },
  WITNESS_POINT: { label: 'Witness point', bg: 'bg-amber-100',  text: 'text-amber-700',  icon: Eye,          desc: 'Client should witness — can proceed if not available' },
  REVIEW:        { label: 'Review',        bg: 'bg-blue-100',   text: 'text-blue-700',   icon: CheckSquare,  desc: 'Document review required' },
  MONITORING:    { label: 'Monitoring',    bg: 'bg-purple-100', text: 'text-purple-700', icon: CheckSquare,  desc: 'Ongoing monitoring' },
}
const INSP_RESULT: Record<string, { bg: string; text: string; label: string }> = {
  PASS:        { bg: 'bg-green-100',  text: 'text-green-700',  label: 'Pass' },
  FAIL:        { bg: 'bg-red-100',    text: 'text-red-700',    label: 'Fail' },
  CONDITIONAL: { bg: 'bg-amber-100',  text: 'text-amber-700',  label: 'Conditional' },
}

// Inspection modal
function InspectModal({ item, onClose, onSubmit }: { item: ITPItem; onClose: () => void; onSubmit: (result: InspectionResult, notes: string) => void }) {
  const [result, setResult] = useState<InspectionResult | ''>('')
  const [notes, setNotes]   = useState('')
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-semibold text-gray-900">Record inspection — {item.itemNumber}</h2>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-gray-50 rounded-lg p-3 text-sm">
            <div className="font-medium text-gray-900 mb-0.5">{item.activity}</div>
            <div className="text-gray-500 text-xs">{item.acceptanceCriteria}</div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Inspection result</label>
            <div className="grid grid-cols-3 gap-2">
              {(['PASS','FAIL','CONDITIONAL'] as const).map(r => {
                const s = INSP_RESULT[r]
                return (
                  <button key={r} type="button" onClick={() => setResult(r)}
                    className={`py-2.5 rounded-lg border-2 text-xs font-bold transition-colors ${result === r ? `border-current ${s.bg} ${s.text}` : 'border-gray-200 text-gray-400 hover:border-gray-300'}`}>
                    {s.label}
                  </button>
                )
              })}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea className="input h-20 resize-none" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Measurements, observations, photo references…" />
          </div>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-3">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={() => { if (result) { onSubmit(result, notes); onClose() } }} disabled={!result}
            className={`btn-primary ${result === 'FAIL' ? 'bg-red-600 hover:bg-red-700' : result === 'PASS' ? 'bg-green-600 hover:bg-green-700' : ''}`}>
            Record {result ? INSP_RESULT[result]?.label : '—'}
          </button>
        </div>
      </div>
    </div>
  )
}



// ─────────────────────────────────────────────────────────────────
// PHASE 5 — HOLD POINT INSPECTION REQUEST
// Formal notice to client/superintendent that a hold point is ready.
// Required by TMR contracts before inspector can attend.
// ─────────────────────────────────────────────────────────────────
function generateHoldPointRequest(item: ITPItem, itp: any) {
  const now    = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const fmtDT  = (iso: string) => iso ? new Date(iso).toLocaleString('en-AU', { dateStyle:'medium', timeStyle:'short' }) : '—'
  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Hold Point Request — ${item.itemNumber}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:11pt;color:#111;margin:22mm}
h1{font-size:22pt;font-weight:900;color:#1B3A6B;margin:0}
.hdr{border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:20px;display:flex;justify-content:space-between;align-items:flex-end}
p{line-height:1.85;margin-bottom:10px}
.hp-banner{background:#b91c1c;color:#fff;border-radius:5px;padding:12px 16px;margin:14px 0;text-align:center;font-size:14pt;font-weight:900}
.info table{width:100%;border-collapse:collapse;margin-bottom:14px}
.info td{padding:5px 8px;border-bottom:1px solid #f3f4f6}.info td:first-child{width:36%;font-weight:bold;color:#555;background:#f9fafb}
.scope-box{border:1px solid #ccc;border-radius:4px;padding:10px 14px;background:#fafafa;margin-bottom:12px;min-height:40px;line-height:1.7}
.sig-row{display:flex;gap:30px;margin-top:14px}
.sig-block{flex:1}.sig-line{border-bottom:1.5px solid #333;height:32px;margin:18px 0 4px}.sig-lbl{font-size:8pt;color:#888}
.resp-box{border:2px solid #1B3A6B;border-radius:5px;padding:12px 16px;margin:14px 0}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:20px;border-top:1px solid #eee;padding-top:7px}
@media print{body{margin:16mm}}
</style></head><body>
<div class="hdr">
  <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 12 345 678 901 · 14 Commerce Dr, Salisbury QLD 4107 · 07 3344 5500</div></div>
  <div style="text-align:right"><div style="font-size:9pt;color:#888">Date: ${now}</div></div>
</div>
<div class="hp-banner">🔴 HOLD POINT INSPECTION REQUEST</div>
<p><strong>ITP: ${itp.itpNumber} · ${itp.jobNumber}</strong></p>
<div class="info"><table>
  <tr><td>ITP reference</td><td><strong>${itp.itpNumber}</strong></td></tr>
  <tr><td>ITP title</td><td>${itp.title}</td></tr>
  <tr><td>Job / project</td><td><strong>${itp.jobNumber}</strong></td></tr>
  <tr><td>Hold point item</td><td><strong>${item.itemNumber}</strong></td></tr>
  <tr><td>Activity</td><td><strong>${item.activity}</strong></td></tr>
  <tr><td>Specification reference</td><td>${item.reference}</td></tr>
  <tr><td>Inspector role required</td><td>${item.inspectorRole === 'CLIENT' ? 'Client / Superintendent representative' : 'Contractor QA'}</td></tr>
  <tr><td>Request date</td><td>${now}</td></tr>
  <tr><td>Proposed inspection date/time</td><td>To be confirmed — please respond within 48 hours</td></tr>
  <tr><td>Location / access</td><td>As per site establishment drawing. Contact WHSO to arrange escort.</td></tr>
</table></div>
<p><strong>Acceptance criteria:</strong></p>
<div class="scope-box">${item.acceptanceCriteria}</div>
<p>MCB Civil Electrical is ready to proceed with the above hold point and requests the attendance of the ${item.inspectorRole === 'CLIENT' ? "Client's Superintendent representative" : 'Contractor QA representative'} to witness/inspect the works described above.</p>
<p><strong>Work cannot proceed beyond this stage until this hold point is formally released.</strong></p>
<div class="resp-box">
  <p style="font-weight:bold;color:#1B3A6B;margin-bottom:8px">Inspector response (to be returned to MCB Civil Electrical)</p>
  <p>☐ Inspection attended — result: ☐ Pass &nbsp; ☐ Conditional &nbsp; ☐ Fail</p>
  <p>☐ Inspection not required — hold point released to proceed</p>
  <p>☐ Re-inspection required — reason: ________________________________________</p>
</div>
<div class="sig-row">
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">MCB Civil Electrical PM/QA — name &amp; date</div></div>
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Inspector — name, organisation, signature &amp; date</div></div>
</div>
<div class="footer">MCB Civil Electrical · Hold Point Inspection Request · ${itp.itpNumber} Item ${item.itemNumber} · ${now}</div>
</body></html>`
  const w = window.open('', '_blank', 'width=960,height=800')
  if (!w) { alert('Pop-up blocked'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}


// ─────────────────────────────────────────────────────────────────
// PHASE 5 — NCR FORMAL DOCUMENT
// ─────────────────────────────────────────────────────────────────
function generateNCRDocument(ncr: any, itp: any) {
  const now  = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const fmtD = (d: string | null) => d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'
  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>NCR — ${ncr.ncrNumber}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:11pt;color:#111;margin:20mm}
h1{font-size:22pt;font-weight:900;color:#1B3A6B;margin:0}h2{font-size:11pt;color:#1B3A6B;border-bottom:1.5px solid #1B3A6B;padding-bottom:3px;margin:14px 0 8px}
.hdr{border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:16px;display:flex;justify-content:space-between;align-items:flex-end}
.ncr-banner{background:#92400e;color:#fff;border-radius:5px;padding:10px 16px;margin:12px 0;font-size:13pt;font-weight:900;text-align:center}
.info table{width:100%;border-collapse:collapse;margin-bottom:12px}
.info td{padding:5px 8px;border-bottom:1px solid #f3f4f6}.info td:first-child{width:36%;font-weight:bold;color:#555;background:#f9fafb}
.scope-box{border:1px solid #ccc;border-radius:4px;padding:10px 14px;background:#fafafa;margin-bottom:12px;min-height:40px;line-height:1.7;white-space:pre-line}
.disp-row{display:flex;gap:10px;margin:8px 0}
.disp-btn{border:1.5px solid #ccc;border-radius:4px;padding:6px 14px;font-size:10pt}
.sig-row{display:flex;gap:30px;margin-top:14px}
.sig-block{flex:1}.sig-line{border-bottom:1.5px solid #333;height:30px;margin:14px 0 4px}.sig-lbl{font-size:8pt;color:#888}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:18px;border-top:1px solid #eee;padding-top:7px}
@media print{body{margin:14mm}}
</style></head><body>
<div class="hdr">
  <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 12 345 678 901 · 14 Commerce Dr, Salisbury QLD 4107</div></div>
  <div style="text-align:right"><div style="font-size:13pt;font-weight:900;color:#92400e">NON-CONFORMANCE REPORT</div><div style="font-family:monospace;font-weight:bold;color:#1B3A6B">${ncr.ncrNumber}</div><div style="font-size:9pt;color:#888">${now}</div></div>
</div>
<div class="info"><table>
  <tr><td>NCR number</td><td><strong>${ncr.ncrNumber}</strong></td><td>Job</td><td><strong>${ncr.jobNumber}</strong></td></tr>
  <tr><td>Date raised</td><td>${fmtD(ncr.date)}</td><td>Raised by</td><td>${ncr.raisedByName}</td></tr>
  <tr><td>Source</td><td>${ncr.source}</td><td>Status</td><td><strong>${ncr.status}</strong></td></tr>
  <tr><td>Location</td><td colspan="3">${ncr.location ?? '—'}</td></tr>
  <tr><td>Specification reference</td><td colspan="3">${ncr.specificationReference ?? '—'}</td></tr>
</table></div>
<h2>Description of non-conformance</h2>
<div class="scope-box">${ncr.description}</div>
<h2>Immediate action taken</h2>
<div class="scope-box">${ncr.immediateAction ?? 'No immediate action recorded — refer to corrective action below.'}</div>
<h2>Disposition</h2>
<div class="disp-row">
  <div class="disp-btn">☐ Use as is</div>
  <div class="disp-btn">☐ Rework</div>
  <div class="disp-btn">☐ Repair</div>
  <div class="disp-btn">☐ Reject / replace</div>
  <div class="disp-btn">☐ Other: _______________</div>
</div>
<h2>Corrective action</h2>
<div class="info"><table>
  <tr><td>Action required</td><td>${ncr.correctiveAction ?? '—'}</td></tr>
  <tr><td>Responsible party</td><td>${ncr.correctiveActionBy ?? '—'}</td></tr>
  <tr><td>Target close date</td><td>${fmtD(ncr.targetCloseDate)}</td></tr>
  <tr><td>Closed date</td><td>${ncr.closedAt ? fmtD(ncr.closedAt) : 'OPEN'}</td></tr>
</table></div>
<div class="sig-row">
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Raised by — name, role &amp; date</div></div>
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">QA Manager / PM review — name &amp; date</div></div>
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Client / superintendent accepted — name &amp; date</div></div>
</div>
<div class="footer">MCB Civil Electrical · NCR ${ncr.ncrNumber} · ${ncr.jobNumber} · ${now} · Retain for quality records minimum 5 years</div>
</body></html>`
  const w = window.open('', '_blank', 'width=960,height=800')
  if (!w) { alert('Pop-up blocked'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}


// ─────────────────────────────────────────────────────────────────
// PHASE 5 — NCR CLOSE-OUT NOTICE
// ─────────────────────────────────────────────────────────────────
function generateNCRCloseOut(ncr: any) {
  const now  = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>NCR Close-Out — ${ncr.ncrNumber}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:11pt;color:#111;margin:22mm}
h1{font-size:22pt;font-weight:900;color:#1B3A6B;margin:0}
.hdr{border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:20px;display:flex;justify-content:space-between;align-items:flex-end}
p{line-height:1.85;margin-bottom:10px}
.co-banner{background:#166534;color:#fff;border-radius:5px;padding:12px 16px;margin:14px 0;text-align:center;font-size:13pt;font-weight:900}
.info table{width:100%;border-collapse:collapse;margin-bottom:14px}
.info td{padding:5px 8px;border-bottom:1px solid #f3f4f6}.info td:first-child{width:36%;font-weight:bold;color:#555;background:#f9fafb}
.scope-box{border:1px solid #ccc;border-radius:4px;padding:10px 14px;background:#fafafa;margin-bottom:12px;min-height:40px;line-height:1.7}
.sig-row{display:flex;gap:30px;margin-top:14px}
.sig-block{flex:1}.sig-line{border-bottom:1.5px solid #333;height:32px;margin:18px 0 4px}.sig-lbl{font-size:8pt;color:#888}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:20px;border-top:1px solid #eee;padding-top:7px}
@media print{body{margin:16mm}}
</style></head><body>
<div class="hdr">
  <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 12 345 678 901 · 14 Commerce Dr, Salisbury QLD 4107 · 07 3344 5500</div></div>
  <div style="text-align:right"><div style="font-size:9pt;color:#888">Date: ${now}</div><div style="font-size:9pt;color:#888">Ref: ${ncr.ncrNumber}</div></div>
</div>
<div class="co-banner">✅ NCR CLOSE-OUT NOTICE</div>
<p>MCB Civil Electrical formally advises that the following Non-Conformance Report has been closed following completion of all required corrective actions and verification of effectiveness.</p>
<div class="info"><table>
  <tr><td>NCR number</td><td><strong>${ncr.ncrNumber}</strong></td></tr>
  <tr><td>Job / project</td><td><strong>${ncr.jobNumber}</strong></td></tr>
  <tr><td>Date raised</td><td>${ncr.date ? new Date(ncr.date).toLocaleDateString('en-AU') : '—'}</td></tr>
  <tr><td>Raised by</td><td>${ncr.raisedByName}</td></tr>
  <tr><td>Close-out date</td><td>${ncr.closedAt ? new Date(ncr.closedAt).toLocaleDateString('en-AU') : now}</td></tr>
</table></div>
<p><strong>Original non-conformance:</strong></p>
<div class="scope-box">${ncr.description}</div>
<p><strong>Corrective action implemented:</strong></p>
<div class="scope-box">${ncr.correctiveAction ?? '—'}</div>
<p><strong>Evidence of effectiveness:</strong></p>
<div class="scope-box">Close-out evidence: ${ncr.closedById ? 'Verified by ' + ncr.closedById : 'Refer to attached evidence'}. All corrective actions confirmed complete to specification.</div>
<p>This NCR is now formally closed. No further action is required. The corrective action taken has been verified as effective in preventing recurrence.</p>
<div class="sig-row">
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">QA Manager / PM — close-out authorised</div></div>
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Client / superintendent — close-out accepted</div></div>
</div>
<div class="footer">MCB Civil Electrical · NCR Close-Out Notice · ${ncr.ncrNumber} · ${ncr.jobNumber} · ${now}</div>
</body></html>`
  const w = window.open('', '_blank', 'width=960,height=750')
  if (!w) { alert('Pop-up blocked'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}


// ─────────────────────────────────────────────────────────────────
// PHASE 5 — ITP COMPLETION REPORT
// ─────────────────────────────────────────────────────────────────
function generateITPCompletionReport(itp: any) {
  const now    = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const fmtD   = (d: string | null) => d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'
  const fmtAUD = (n: number) => n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })
  const items  = itp.items ?? []
  const holdPoints     = items.filter((i: any) => i.inspectionType === 'HOLD_POINT')
  const releasedHP     = holdPoints.filter((i: any) => i.holdPointReleased)
  const outstandingHP  = holdPoints.filter((i: any) => !i.holdPointReleased)
  const allPassed      = items.filter((i: any) => i.latestResult === 'PASS').length
  const allInspected   = items.filter((i: any) => i.latestResult !== null).length

  const itemRows = items.map((item: any, idx: number) => {
    const typeColor = item.inspectionType === 'HOLD_POINT' ? '#b91c1c' : item.inspectionType === 'WITNESS_POINT' ? '#d97706' : '#1d4ed8'
    const resultColor = item.latestResult === 'PASS' ? '#166534' : item.latestResult === 'FAIL' ? '#b91c1c' : item.latestResult ? '#92400e' : '#6b7280'
    const hpColor = item.holdPointReleased ? '#166534' : item.inspectionType === 'HOLD_POINT' ? '#b91c1c' : '#6b7280'
    return `<tr style="background:${idx%2===0?'#fff':'#f9fafb'}">
      <td style="padding:6px 8px;font-weight:bold">${item.itemNumber}</td>
      <td style="padding:6px 10px">${item.activity}</td>
      <td style="padding:6px 8px;text-align:center;font-size:9pt;color:${typeColor};font-weight:bold">${item.inspectionType?.replace(/_/g,' ')}</td>
      <td style="padding:6px 8px;text-align:center;font-weight:bold;color:${resultColor}">${item.latestResult ?? 'Pending'}</td>
      <td style="padding:6px 8px;text-align:center;font-size:9pt">${item.latestInspector ?? '—'}</td>
      <td style="padding:6px 8px;text-align:center;font-size:9pt">${fmtD(item.latestDate)}</td>
      <td style="padding:6px 8px;text-align:center;font-weight:bold;color:${hpColor}">${item.inspectionType === 'HOLD_POINT' ? (item.holdPointReleased ? '✓ Released' : '⚠ OPEN') : '—'}</td>
    </tr>`
  }).join('')

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>ITP Completion Report — ${itp.itpNumber}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10pt;color:#111;margin:14mm}
h1{font-size:20pt;font-weight:900;color:#1B3A6B;margin:0}h2{font-size:10.5pt;color:#1B3A6B;border-bottom:1.5px solid #1B3A6B;padding-bottom:3px;margin:12px 0 6px}
.hdr{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:12px}
.kpi{display:flex;gap:10px;margin-bottom:12px}
.kpi-box{flex:1;border:1px solid #e5e7eb;border-radius:5px;padding:8px;text-align:center}
.kpi-v{font-size:22pt;font-weight:900}
.info table{width:100%;border-collapse:collapse;margin-bottom:10px}
.info td{padding:4px 8px;border-bottom:1px solid #f3f4f6}.info td:first-child{width:30%;font-weight:bold;color:#555}
table.items{width:100%;border-collapse:collapse;font-size:9.5pt}
th{background:#1B3A6B;color:#fff;padding:5px 8px;text-align:left}
.sig-row{display:flex;gap:30px;margin-top:14px}
.sig-block{flex:1}.sig-line{border-bottom:1.5px solid #333;height:28px;margin:12px 0 4px}.sig-lbl{font-size:8pt;color:#888}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:16px;border-top:1px solid #eee;padding-top:6px}
@media print{body{margin:10mm}}
</style></head><body>
<div class="hdr">
  <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 12 345 678 901</div></div>
  <div style="text-align:right"><div style="font-size:16pt;font-weight:900;color:#1B3A6B">ITP COMPLETION REPORT</div>
    <div style="font-family:monospace;font-weight:bold;color:#1B3A6B">${itp.itpNumber}</div>
    <div style="font-size:9pt;color:#888">As at: ${now}</div></div>
</div>
<div class="info"><table>
  <tr><td>ITP title</td><td><strong>${itp.title}</strong></td></tr>
  <tr><td>Job / project</td><td><strong>${itp.jobNumber}</strong></td></tr>
  <tr><td>ITP revision</td><td>Rev ${itp.revision ?? 1}</td></tr>
  <tr><td>Approved by</td><td>${itp.approvedByName ?? '—'}</td></tr>
  <tr><td>ITP status</td><td><strong>${itp.status}</strong></td></tr>
</table></div>
<div class="kpi">
  <div class="kpi-box"><div class="kpi-v" style="color:#1B3A6B">${items.length}</div><div style="font-size:9pt;color:#6b7280">Total items</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:#166534">${allInspected}</div><div style="font-size:9pt;color:#6b7280">Inspected</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:#166534">${allPassed}</div><div style="font-size:9pt;color:#6b7280">Passed</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:${outstandingHP.length > 0?'#b91c1c':'#166534'}">${releasedHP.length}/${holdPoints.length}</div><div style="font-size:9pt;color:#6b7280">Hold points released</div></div>
  <div class="kpi-box"><div class="kpi-v" style="color:${outstandingHP.length > 0?'#b91c1c':'#166534'}">${outstandingHP.length}</div><div style="font-size:9pt;color:#6b7280">Outstanding HPs</div></div>
</div>
${outstandingHP.length > 0 ? `<div style="background:#fee2e2;border:1.5px solid #fca5a5;border-radius:5px;padding:8px 14px;margin-bottom:10px;color:#b91c1c;font-weight:bold">⚠ ${outstandingHP.length} hold point${outstandingHP.length!==1?'s':''} outstanding — practical completion cannot be certified until all hold points are released: ${outstandingHP.map((h:any) => h.itemNumber+' '+h.activity).join(', ')}</div>` : `<div style="background:#dcfce7;border:1.5px solid #86efac;border-radius:5px;padding:8px 14px;margin-bottom:10px;color:#166534;font-weight:bold">✅ All hold points released — ITP complete</div>`}
<h2>Inspection items</h2>
<table class="items"><thead><tr>
  <th style="width:6%">Item</th><th style="width:30%">Activity</th><th style="width:13%;text-align:center">Type</th>
  <th style="width:9%;text-align:center">Result</th><th style="width:16%;text-align:center">Inspector</th>
  <th style="width:12%;text-align:center">Date</th><th style="width:14%;text-align:center">Hold point</th>
</tr></thead><tbody>${itemRows}</tbody></table>
<div class="sig-row">
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">QA Manager / PM — certification</div></div>
  <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Client / superintendent — accepted</div></div>
</div>
<div class="footer">MCB Civil Electrical · ITP Completion Report · ${itp.itpNumber} · ${itp.jobNumber} · ${now}</div>
</body></html>`
  const w = window.open('', '_blank', 'width=1100,height=800')
  if (!w) { alert('Pop-up blocked'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}


// ─────────────────────────────────────────────────────────────────
// PHASE 5 — NCR REGISTER CSV
// ─────────────────────────────────────────────────────────────────
function exportNCRRegisterCSV(ncrs: any[]) {
  const fmtD = (d: string | null) => d ? new Date(d).toLocaleDateString('en-AU') : '—'
  const rows: string[][] = [
    ['NCR Number','Job','Date Raised','Raised By','Source','Description',
     'Location','Specification Reference','Corrective Action','Responsible Party',
     'Target Close Date','Status','Closed Date']
  ]
  for (const n of ncrs) {
    rows.push([
      n.ncrNumber, n.jobNumber, fmtD(n.date), n.raisedByName ?? '',
      n.source ?? '', n.description ?? '', n.location ?? '',
      n.specificationReference ?? '', n.correctiveAction ?? '',
      n.correctiveActionBy ?? '', fmtD(n.targetCloseDate),
      n.status ?? '', fmtD(n.closedAt),
    ])
  }
  const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join(String.fromCharCode(10))
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href = url; a.download = `MCB_NCR_Register_${new Date().toISOString().slice(0,10)}.csv`
  document.body.appendChild(a); a.click()
  document.body.removeChild(a); URL.revokeObjectURL(url)
}
export function ITP() {
  const { isTier } = useAuthStore()
  const [itp, setItp]             = useState<ITPRecord>(MOCK_ITP as ITPRecord)
  const [inspecting, setInspecting] = useState<ITPItem | null>(null)
  const [handoverFlagged, setHandoverFlagged] = useState(false)
  const canRelease = isTier('B_PM')
  const canInspect = isTier('D_FIELD')

  // Live ITP records from Neon — via itpService
  const { data: liveITPRecords = [] } = useQuery({
    queryKey: ['itp-records'],
    queryFn:  () => fetchITPRecords(),
    staleTime: 60_000,
  })

  // P3-3: When all hold points released, set handover readiness flag on the job
  useEffect(() => {
    const holdPoints = itp.items.filter(i => i.inspectionType === 'HOLD_POINT')
    if (holdPoints.length > 0 && holdPoints.every(i => i.holdPointReleased)) {
      setHandoverReadyFlag(itp.jobId)
      setHandoverFlagged(true)
    }
  }, [itp.items, itp.jobId])

  const openHoldPoints = itp.items.filter(i => i.inspectionType === 'HOLD_POINT' && !i.holdPointReleased)
  const openWitnessPoints = itp.items.filter(i => i.inspectionType === 'WITNESS_POINT' && !i.latestResult)

  const recordInspection = (itemId: string, result: InspectionResult, notes: string) => {
    const { user } = useAuthStore.getState()
    setItp(prev => ({
      ...prev,
      items: prev.items.map(i => i.id !== itemId ? i : {
        ...i,
        latestResult: result,
        latestInspector: user?.fullName ?? 'Current User',
        latestDate: new Date().toISOString().slice(0, 10),
        notes: notes || i.notes,
      })
    }))
  }

  const releaseHoldPoint = (itemId: string) => {
    const { user } = useAuthStore.getState()
    const item = itp.items.find(i => i.id === itemId)   // capture before state update
    setItp(prev => ({
      ...prev,
      items: prev.items.map(i => i.id !== itemId ? i : {
        ...i,
        holdPointReleased: true,
        releasedBy: user?.fullName ?? 'PM',
        releasedAt: new Date().toISOString(),
      })
    }))
    // Fire crew notification for on-site team
    if (item) fireHoldPointReleaseAlert(item, itp.jobId, itp.jobNumber ?? '', user?.fullName ?? 'PM')

    // Write-through to Neon: find matching live record by title and witness PASS
    const matchingNeonRecord = (liveITPRecords as any[]).find(
      (r: any) => r.title?.toLowerCase().includes((item?.activity ?? '').toLowerCase().slice(0, 20))
    )
    if (matchingNeonRecord?.id) {
      witnessITPNeon(matchingNeonRecord.id, {
        witnessedById: user?.id ?? 'pm',
        result: 'PASS',
        notes: `Hold point released by ${user?.fullName ?? 'PM'}`,
      }).catch((err: any) => console.warn('[ITP] Neon witness write failed:', err))
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">ITP &amp; Hold Points</h1>
        <p className="text-sm text-gray-500 mt-0.5">Inspection &amp; Test Plans · {itp.itpNumber}</p>
      </div>

      {/* Open hold points alert */}
      {openHoldPoints.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-4">
          <div className="flex items-center gap-2 mb-2">
            <Lock className="w-4 h-4 text-red-600" />
            <span className="font-semibold text-red-800">{openHoldPoints.length} open hold point{openHoldPoints.length !== 1 ? 's' : ''} — work cannot proceed on these activities</span>
          </div>
          <div className="space-y-1">
            {openHoldPoints.map(h => (
              <div key={h.id} className="flex items-center justify-between bg-red-100 rounded-lg px-3 py-2">
                <span className="text-sm text-red-800"><strong>{h.itemNumber}</strong> {h.activity}</span>
                {canRelease && h.latestResult === 'PASS' && (
                  <button onClick={() => releaseHoldPoint(h.id)} className="text-xs bg-green-600 text-white px-3 py-1 rounded-lg font-medium hover:bg-green-700">Release hold point</button>
                )}
                {!h.latestResult && <span className="text-xs text-red-600 font-medium">Inspection required first</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {openHoldPoints.length === 0 && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-800">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />All hold points released — work may proceed on all activities
        </div>
      )}

      {/* P3-3: Handover readiness flag banner */}
      {handoverFlagged && (
        <div className="flex items-center gap-3 bg-teal-50 border border-teal-300 rounded-xl px-4 py-3 text-sm text-teal-800">
          <CheckCircle className="w-4 h-4 text-teal-600 flex-shrink-0" />
          <div>
            <span className="font-semibold">Handover readiness flag set for {itp.jobNumber}.</span>
            {' '}All ITP hold points released — this job is now eligible for handover. The Handover module will show {itp.jobNumber} as ready for close-out.
          </div>
        </div>
      )}

      {/* P3-7: WI completion → ITP witness points ready */}
      {isWICompletedForJob(itp.jobId) && (
        <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-300 rounded-xl px-4 py-3 text-sm text-indigo-800">
          <CheckSquare className="w-4 h-4 text-indigo-600 flex-shrink-0" />
          Work instruction steps completed for {itp.jobNumber} — ITP witness points may now be ready for recording. Review WITNESS_POINT items below and record inspection results.
        </div>
      )}

      {/* ITP header */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <span className="font-bold text-gray-900">{itp.itpNumber}</span>
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">{itp.status}</span>
              </div>
              <p className="text-sm text-gray-600">{itp.title} — {itp.jobNumber}</p>
              <p className="text-xs text-gray-400 mt-0.5">Approved by {itp.approvedByName} · Rev {itp.revision}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => generateITPCompletionReport(itp)} className="btn-secondary text-xs flex items-center gap-1.5">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                ITP completion report
              </button>
              <button onClick={() => exportNCRRegisterCSV(MOCK_NCRS)} className="btn-secondary text-xs flex items-center gap-1.5">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                NCR register
              </button>
            </div>
          </div>
        </div>

        {/* ITP items table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ minWidth: 700 }}>
            <thead>
              <tr className="bg-gray-50 border-b text-xs text-gray-500">
                <th className="text-left px-4 py-3 font-medium w-12">No.</th>
                <th className="text-left px-3 py-3 font-medium">Activity</th>
                <th className="text-left px-3 py-3 font-medium w-28">Type</th>
                <th className="text-left px-3 py-3 font-medium w-28">Inspector</th>
                <th className="text-left px-3 py-3 font-medium w-28">Result</th>
                <th className="text-left px-3 py-3 font-medium w-32">Hold point</th>
                <th className="px-4 py-3 w-32"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {itp.items.map(item => {
                const it  = INSP_TYPE[item.inspectionType] ?? INSP_TYPE.REVIEW
                const res = item.latestResult ? INSP_RESULT[item.latestResult] : null
                const Icon = it.icon
                const isBlocking = item.inspectionType === 'HOLD_POINT' && !item.holdPointReleased
                return (
                  <tr key={item.id} className={`${isBlocking ? 'bg-red-50/50' : ''} hover:bg-gray-50/80`}>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">{item.itemNumber}</td>
                    <td className="px-3 py-3">
                      <div className="font-medium text-gray-900 text-sm">{item.activity}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{item.reference} · {item.frequency}</div>
                      <div className="text-xs text-gray-400 mt-0.5 italic">{item.acceptanceCriteria}</div>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex items-center gap-1 w-fit ${it.bg} ${it.text}`}>
                        <Icon className="w-3 h-3" />{it.label}
                      </span>
                      <div className="text-xs text-gray-400 mt-0.5">{item.inspectorRole}</div>
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-500">{item.latestInspector ?? '—'}</td>
                    <td className="px-3 py-3">
                      {res
                        ? <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${res.bg} ${res.text}`}>{res.label}</span>
                        : <span className="text-xs text-gray-300">Not inspected</span>
                      }
                      {item.latestDate && <div className="text-xs text-gray-400 mt-0.5">{formatDate(item.latestDate)}</div>}
                    </td>
                    <td className="px-3 py-3 text-xs">
                      {item.inspectionType === 'HOLD_POINT' ? (
                        item.holdPointReleased
                          ? <span className="text-green-600 font-medium flex items-center gap-1"><CheckCircle className="w-3 h-3" />Released</span>
                          : <span className="text-red-600 font-bold flex items-center gap-1"><Lock className="w-3 h-3" />LOCKED</span>
                      ) : '—'}
                      {item.releasedBy && <div className="text-gray-400 mt-0.5">{item.releasedBy}</div>}
                    </td>
                    <td className="px-4 py-3">
                      {!item.latestResult && canInspect && (
                        <button onClick={() => setInspecting(item)} className="text-xs btn-secondary py-1">Inspect</button>
                      )}
                      {item.latestResult === 'PASS' && item.inspectionType === 'HOLD_POINT' && !item.holdPointReleased && canRelease && (
                        <div className="flex gap-1.5"><button onClick={() => releaseHoldPoint(item.id)} className="text-xs bg-green-100 text-green-700 hover:bg-green-200 px-2 py-1 rounded-lg font-medium transition-colors">Release</button><button onClick={e => { e.stopPropagation(); generateHoldPointRequest(item, itp) }} className="text-xs bg-red-50 text-red-700 hover:bg-red-100 px-2 py-1 rounded-lg font-medium border border-red-200 transition-colors">HP request</button></div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Live ITP records from Neon */}
      {liveITPRecords.length > 0 && (
        <div className="bg-white border border-blue-100 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b bg-blue-50 flex items-center gap-2">
            <CheckSquare className="w-4 h-4 text-blue-600" />
            <h3 className="font-semibold text-blue-900 text-sm">Live ITP records from Neon ({liveITPRecords.length})</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {liveITPRecords.map((r: any) => (
              <div key={r.id} className="px-5 py-3 flex items-center gap-4">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  r.status === 'PASSED' ? 'bg-green-100 text-green-700' :
                  r.status === 'FAILED' ? 'bg-red-100 text-red-700' :
                  r.status === 'HOLD'   ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-600'
                }`}>{r.status ?? 'PENDING'}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{r.title}</div>
                  {r.holdType && <div className="text-xs text-gray-400">{r.holdType}</div>}
                </div>
                {r.witnessedAt && <div className="text-xs text-gray-400">{new Date(r.witnessedAt).toLocaleDateString('en-AU')}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* NCRs */}
      {MOCK_NCRS.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b flex items-center justify-between">
            <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /><h3 className="font-semibold text-gray-900">Non-Conformance Reports</h3></div>
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">{MOCK_NCRS.filter(n => n.status === 'OPEN').length} open</span>
          </div>
          <div className="divide-y divide-gray-100">
            {MOCK_NCRS.map(ncr => (
              <div key={ncr.id} className="px-5 py-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-sm text-gray-900">{ncr.ncrNumber}</span>
                  <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">{ncr.status}</span>
                  <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">{ncr.source}</span>
                  <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full">{ncr.jobNumber}</span>
                </div>
                <p className="text-sm text-gray-700">{ncr.description}</p>
                <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
                  <span>Raised: {formatDate(ncr.date)}</span>
                  <span>Action: {ncr.correctiveActionBy}</span>
                  <span>Due: {formatDate(ncr.targetCloseDate)}</span>
                  <button onClick={() => generateNCRDocument(ncr, itp)} className="text-xs bg-amber-50 text-amber-700 hover:bg-amber-100 px-2 py-1 rounded-lg font-medium border border-amber-200">NCR document</button>
                  {ncr.status === 'CLOSED' && <button onClick={() => generateNCRCloseOut(ncr)} className="text-xs bg-green-50 text-green-700 hover:bg-green-100 px-2 py-1 rounded-lg font-medium border border-green-200">Close-out notice</button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {inspecting && (
        <InspectModal item={inspecting} onClose={() => setInspecting(null)}
          onSubmit={(result, notes) => { recordInspection(inspecting.id, result, notes); setInspecting(null) }} />
      )}
    </div>
  )
}
